import { Link, router } from "@inertiajs/react";
import { motion } from "framer-motion";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ShoppingCart, Star } from "lucide-react";
import { Product, StoreSettings } from "@/types";

const ProductCard = ({
  product,
  className,
  store_settings,
}: {
  product: Product;
  className?: string;
  store_settings: StoreSettings;
}) => {
  const productUrl =
    product.type === "FeaturedProduct"
      ? `/productos-exclusivos/${product.slug}`
      : `/${product.slug}`;

  const hasActivePromotion = product.has_active_promotion;
  const discountPercentage = hasActivePromotion
    ? Math.round(
        ((Number(product.price) - (product.sale_price || 0)) /
          Number(product.price)) *
          100,
      )
    : 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className={`block h-full ${className}`}
    >
      <Link href={productUrl} className="block h-full">
        <Card className="group relative flex flex-col overflow-hidden transition-all duration-500 hover-lift hover:shadow-elegant-lg h-full bg-white dark:bg-neutral-900 shadow-sm hover:shadow-2xl hover:shadow-purple-500/10 border-0">
        {/* Discount Badge - Only prominent when there's a significant discount */}
        {hasActivePromotion && discountPercentage >= 10 && (
          <div className="absolute top-2 left-2 z-20">
            <Badge className="bg-red-500 text-white font-bold px-1.5 py-0.5 text-xs shadow-lg">
              -{discountPercentage}%
            </Badge>
          </div>
        )}

        {/* Category Badge - Subtle, not overwhelming */}
        <div className="absolute top-2 right-2 z-20">
          <Badge className="bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 font-medium px-2 py-0.5 text-xs">
            {product.category_name}
          </Badge>
        </div>



        <CardHeader className="p-0 relative">
          <div className="aspect-square w-full overflow-hidden bg-neutral-50 dark:bg-neutral-800 relative">
            <img
              src={
                product.images?.[0] ||
                product.image_url ||
                "https://picsum.photos/300"
              }
              alt={product.name}
              className="w-full h-full object-contain transition-all duration-500 group-hover:scale-105"
              style={{
                objectFit: "contain",
                objectPosition: "center",
              }}
            />
            {/* Multiple image indicator */}
            {product.images && product.images.length > 1 && (
              <div className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                <svg
                  className="w-3 h-3"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" />
                </svg>
                +{product.images.length - 1}
              </div>
            )}
            {/* Subtle overlay for better interaction feedback */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/10 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          </div>
        </CardHeader>

        <CardContent className="flex-grow pb-0 pt-2.5 lg:px-4 lg:pt-4 relative">
          {product.brand_name && (
            <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 uppercase tracking-wider">
              {product.brand_name}
            </p>
          )}
          <CardTitle className="text-sm lg:text-base font-semibold mb-2 line-clamp-2 text-neutral-900 dark:text-neutral-100 group-hover:text-neutral-700 dark:group-hover:text-neutral-300 transition-colors duration-300 leading-tight">
            {product.name}
          </CardTitle>

          {/* Rating Stars */}
          <div className="flex items-center gap-1 mb-3">
            {[1, 2, 3, 4, 5].map((star) => (
              <motion.div
                key={star}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: star * 0.1, type: "spring", stiffness: 200 }}
              >
                <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
              </motion.div>
            ))}
            <span className="text-xs text-neutral-500 dark:text-neutral-400 ml-1">(4.8)</span>
          </div>

          {/* Description removed as requested */}
        </CardContent>

        <CardFooter className="p-2.5 lg:p-4 pt-0 mt-auto">
          <div className="flex flex-col gap-2.5 lg:gap-3 justify-between items-center w-full">
            {/* Price Section - Clear hierarchy */}
            <div className="flex flex-col gap-1 justify-between items-center w-full">
              {/* Precio Online/Oferta - Always shown */}
              <div className="flex items-start justify-center w-full">
                <div
                  className={`inline-flex flex-col items-center px-2 py-1 rounded-lg mr-2 ${
                    hasActivePromotion
                      ? "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400"
                      : "bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-400"
                  }`}
                >
                  {hasActivePromotion ? (
                    <>
                      <span className="text-xs font-medium leading-tight">
                        Precio
                      </span>
                      <span className="text-xs font-medium leading-tight">
                        Oferta
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="text-xs font-medium leading-tight">
                        Precio
                      </span>
                      <span className="text-xs font-medium leading-tight">
                        Online
                      </span>
                    </>
                  )}
                </div>
                <p
                  className={`text-sm lg:text-base font-bold whitespace-nowrap flex-shrink-0 ${
                    hasActivePromotion
                      ? "text-red-600 dark:text-red-400"
                      : "text-neutral-900 dark:text-neutral-100"
                  }`}
                >
                  S/.{" "}
                  {hasActivePromotion
                    ? (Number(product.sale_price) || 0).toFixed(2)
                    : (Number(product.price) || 0).toFixed(2)}
                </p>
              </div>

              {/* Precio Regular - Simple text only */}
              <div className="flex items-center justify-center w-full mt-0.5">
                <span className="text-xs text-neutral-500 dark:text-neutral-400 mr-1">
                  Precio Regular
                </span>
                <p
                  className={`text-xs text-neutral-500 dark:text-neutral-400 whitespace-nowrap`}
                >
                  S/.{" "}
                  {hasActivePromotion
                    ? (Number(product.price) || 0).toFixed(2)
                    : (
                        Number(product.market_price) ||
                        Number(product.price) ||
                        0
                      ).toFixed(2)}
                </p>
              </div>
            </div>

            {/* Action Button - Mobile vs Desktop different layouts */}
            {product.cart_enabled ? (
              <Button
                size="sm"
                className="w-full bg-neutral-900 dark:bg-neutral-100 hover:bg-neutral-100 text-white dark:text-black hover:text-black font-medium transition-all duration-300 hover:scale-105 hover:shadow-lg text-xs lg:text-sm px-2 lg:px-4 h-8 lg:h-9"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  router.post(
                    "/cart/items",
                    { product_id: product.id },
                    { preserveScroll: true },
                  );
                }}
              >
                {/* Mobile Layout: "Añadir" + Icon */}
                <span className="lg:hidden flex items-center gap-1">
                  Añadir
                  <ShoppingCart className="h-3 w-3" />
                </span>

                {/* Desktop Layout: Icon + "Añadir al Carro" */}
                <span className="hidden lg:flex items-center gap-2">
                  <ShoppingCart className="h-4 w-4" />
                  Añadir al Carro
                </span>
              </Button>
            ) : (
              <Button
                size="sm"
                className="w-full bg-neutral-900 dark:bg-neutral-100 hover:bg-neutral-100 text-white dark:text-black hover:text-black font-medium transition-all duration-300 hover:scale-105 hover:shadow-lg text-xs lg:text-sm px-2 lg:px-4 h-8 lg:h-9"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  window.open(
                    `https://wa.me/${store_settings.whatsapp_number}`,
                    "_blank",
                  );
                }}
              >
                {/* Mobile Layout: "Cotizar" + Icon */}
                <span className="lg:hidden flex items-center gap-1">
                  Cotizar
                  <span className="text-sm">💬</span>
                </span>

                {/* Desktop Layout: Icon + "Cotizar" */}
                <span className="hidden lg:flex items-center gap-2">
                  💬 Cotizar
                </span>
              </Button>
            )}
          </div>
        </CardFooter>

        {/* Subtle hover glow effect */}
        <div className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none shadow-inner" />
      </Card>
    </Link>
    </motion.div>
  );
};

export default ProductCard;
